/**
 * Fils de discussion — logique PURE (classement et regroupement), testée
 * sans réseau.
 *
 * Deux familles de fils :
 *   • l'ASSISTANCE : une conversation entre le dispatch et UNE PERSONNE SOUS UNE
 *     CASQUETTE (migration 0053). La même personne peut être livreur ET cliente :
 *     elle a alors deux fils distincts, « Livreur » et « Client », et la réponse du
 *     dispatch n'arrive que dans l'app correspondante ;
 *   • les échanges CLIENT ↔ LIVREUR d'une commande (0040), que le dispatch LIT
 *     pour arbitrer un litige, sans y écrire.
 *
 * ⚠️ On ne déduit plus « qui est le dispatch » d'une liste de membres du staff :
 * chaque message porte ses deux casquettes (`casquette_expediteur`,
 * `casquette_destinataire` ; NULL = l'équipe). Un membre du staff qui commande
 * écrit en tant que client — l'ancienne règle aurait rangé son message du côté
 * du dispatch.
 */

/** Au nom de quel rôle une personne écrit (ou reçoit). */
export type Casquette = 'client' | 'livreur' | 'marchand'

export const LIBELLE_CASQUETTE: Record<Casquette, string> = {
  client: 'Client',
  livreur: 'Livreur',
  marchand: 'Marchand',
}

export function estCasquette(x: unknown): x is Casquette {
  return x === 'client' || x === 'livreur' || x === 'marchand'
}

/** Clé d'un fil d'assistance : la personne ET sa casquette. */
export function cleFil(personne: string, casquette: Casquette): string {
  return `${personne}|${casquette}`
}

/** L'inverse de `cleFil` ; `null` si la clé n'en est pas une. */
export function lireCleFil(cle: string): { personne: string; casquette: Casquette } | null {
  const i = cle.lastIndexOf('|')
  if (i <= 0) return null
  const casquette = cle.slice(i + 1)
  return estCasquette(casquette) ? { personne: cle.slice(0, i), casquette } : null
}

/** Une ligne de `chat_messages`, telle que la lit la console. */
export interface LigneChat {
  id: number
  order_id: string | null
  sender_id: string | null
  recipient_id: string | null
  body: string
  is_system: boolean
  read_at: string | null
  created_at: string
  /** NULL = l'équipe (dispatch) ou un message système. */
  casquette_expediteur: Casquette | null
  /** NULL = adressé au dispatch. */
  casquette_destinataire: Casquette | null
}

/** Les colonnes que la console lit dans `chat_messages`. */
export const COLONNES_CHAT =
  'id,order_id,sender_id,recipient_id,body,is_system,read_at,created_at,casquette_expediteur,casquette_destinataire'

/**
 * Un échange client ↔ livreur : lié à une commande, entre deux PERSONNES qui
 * écrivent chacune sous une casquette (aucune extrémité n'est le dispatch).
 * Les messages système ne sont jamais des échanges entre personnes.
 */
export function estEchangeClientLivreur(
  m: Pick<LigneChat, 'order_id' | 'sender_id' | 'recipient_id' | 'is_system' | 'casquette_expediteur' | 'casquette_destinataire'>,
): boolean {
  return (
    !m.is_system &&
    m.order_id !== null &&
    m.sender_id !== null &&
    m.recipient_id !== null &&
    m.casquette_expediteur !== null &&
    m.casquette_destinataire !== null
  )
}

/** Un message d'un fil d'assistance, vu du dispatch. */
export interface MessageFil {
  id: string
  de: 'dispatch' | 'them' | 'system'
  texte: string
  envoyeLe: string
  lu: boolean
}

/**
 * Regroupe l'ASSISTANCE par (personne, casquette).
 *
 *   • message d'une personne (casquette d'expéditeur posée) → fil (expéditeur, sa casquette) ;
 *   • message du dispatch ou du système (sans casquette d'expéditeur) → fil
 *     (destinataire, casquette visée).
 * Les échanges client ↔ livreur sont écartés (`regrouperDiscussions`), comme tout
 * message qui ne désigne ni personne ni casquette (staff → staff).
 */
export function regrouperFils(lignes: readonly LigneChat[]): Record<string, MessageFil[]> {
  const fils: Record<string, MessageFil[]> = {}
  const triees = [...lignes].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id - b.id)
  for (const m of triees) {
    if (estEchangeClientLivreur(m)) continue
    const duneP = m.casquette_expediteur !== null
    const personne = duneP ? m.sender_id : m.recipient_id
    const casquette = duneP ? m.casquette_expediteur : m.casquette_destinataire
    if (!personne || !casquette) continue
    ;(fils[cleFil(personne, casquette)] ??= []).push({
      id: String(m.id),
      de: m.is_system ? 'system' : duneP ? 'them' : 'dispatch',
      texte: m.body,
      envoyeLe: m.created_at,
      lu: Boolean(m.read_at),
    })
  }
  return fils
}

/** Ce qu'il faut savoir d'une commande pour nommer les deux parties. */
export interface InfosCommande {
  id: string
  ref: string | null
  clientId: string | null
  livreurId: string | null
  clientNom: string
  livreurNom: string
}

export interface MessageDiscussion {
  id: number
  de: 'client' | 'livreur'
  /** Nom de la personne qui parle. */
  nom: string
  texte: string
  envoyeLe: string
}

export interface DiscussionCommande {
  orderId: string
  ref: string | null
  clientNom: string
  livreurNom: string
  messages: MessageDiscussion[]
  /** Horodatage du dernier message — sert à classer les fils, le plus récent en haut. */
  dernierLe: string
}

/**
 * Regroupe les échanges client ↔ livreur PAR COMMANDE.
 *
 * Qui parle : l'expéditeur est le client de la commande, sinon c'est un
 * livreur — le livreur actuel, ou un précédent si la course a été réaffectée
 * entre-temps (on le dit plutôt que de lui prêter le nom du livreur actuel).
 *
 * Les messages dont la commande est inconnue sont écartés : sans elle, on ne
 * saurait ni nommer les parties ni intituler le fil.
 */
export function regrouperDiscussions(
  lignes: readonly LigneChat[],
  commandes: ReadonlyMap<string, InfosCommande>,
): DiscussionCommande[] {
  const parCommande = new Map<string, DiscussionCommande>()
  const triees = [...lignes].sort(
    (a, b) => a.created_at.localeCompare(b.created_at) || a.id - b.id,
  )

  for (const m of triees) {
    if (!estEchangeClientLivreur(m) || !m.order_id) continue
    const infos = commandes.get(m.order_id)
    if (!infos) continue

    // La casquette dit qui parle ; repli sur l'identité pour une ligne sans casquette.
    const duClient = m.casquette_expediteur ? m.casquette_expediteur === 'client' : m.sender_id === infos.clientId
    const message: MessageDiscussion = {
      id: m.id,
      de: duClient ? 'client' : 'livreur',
      nom: duClient
        ? infos.clientNom
        : m.sender_id === infos.livreurId
          ? infos.livreurNom
          : 'Livreur précédent',
      texte: m.body,
      envoyeLe: m.created_at,
    }

    const fil = parCommande.get(m.order_id) ?? {
      orderId: m.order_id,
      ref: infos.ref,
      clientNom: infos.clientNom,
      livreurNom: infos.livreurNom,
      messages: [],
      dernierLe: m.created_at,
    }
    fil.messages.push(message)
    fil.dernierLe = m.created_at
    parCommande.set(m.order_id, fil)
  }

  return [...parCommande.values()].sort((a, b) => b.dernierLe.localeCompare(a.dernierLe))
}

/** Intitulé d'un fil, comme sur l'écran : « Commande #EAT-AB12CD · Client ↔ Livreur ». */
export function titreDiscussion(d: Pick<DiscussionCommande, 'ref' | 'orderId'>): string {
  const ref = d.ref ?? d.orderId.slice(0, 6).toUpperCase()
  return `Commande #${ref} · Client ↔ Livreur`
}
